package com.exura.pages.import

import androidx.compose.runtime.*
import com.exura.components.buttons.AppButton
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonGhostVariant
import com.exura.components.buttons.ButtonPrimaryVariant
import com.exura.components.buttons.ButtonRoundVariant
import com.exura.components.dialogs.DialogPanelLargeVariant
import com.exura.components.icons.UntiledIcon
import com.exura.components.inputs.InlineInputGhostVariant
import com.exura.components.inputs.InlineTextInput
import com.exura.components.inputs.InlineTextInputWithAction
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraCommentaryTextVariant
import com.exura.components.pageTypography.ExuraEmphasisedTextVariant
import com.exura.components.pageTypography.ExuraErrorTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.ExuraUnderwriteTextVariant
import com.exura.components.pageTypography.GeneralCardEntry
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.HoverableListEntryVariant
import com.exura.components.pageTypography.InteractiveCardEntryVariant
import com.exura.components.pageTypography.NumberedStep
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.TitledSection
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.demosets.domain.model.DemoSet
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.import.importLocalization
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.presentation.PlanDefinitionDetails
import com.exura.features.plandefinition.presentation.PlanDefinitionListSelectionDialogContent
import com.exura.navigation.Route
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.exura.util.lineClamp
import com.varabyte.kobweb.compose.css.AlignSelf
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignSelf
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input

@Page(Route.IMPORT_PLANS)
@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.ImportPlansPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val controller = rememberImportController(scope, context)
    val state = controller.uiState

    Column(
        modifier = Modifier.fillMaxSize()
    ) {
        PageNavigationHeaderSection(
            importLocalization.current.importPlans,
            isPageWorking = state.isLoading,
            onBack = null,
        )

        when {
            state.isLoading && state.demoSets.isEmpty() -> Loader()
            state.error != null -> PageErrorContent(errorMessage = state.error)
            else -> ImportPlansView(
                modifier = Modifier.weight(1f),
                state = state,
                handleEvent = controller::handleEvent,
            )
        }
    }
}

@Composable
private fun NavigableLayoutScope.ImportPlansView(
    modifier: Modifier = Modifier,
    state: ImportUiState,
    handleEvent: (ImportEvent) -> Unit,
) {
    val onImportPublicDemoSet: suspend (DemoSet) -> Unit = { demoSet: DemoSet ->
        suspendCancellableCoroutine { cont ->
            val successMsg = "✅ ${
                importLocalization.current.success.demoSetImportedWithCount(
                    demoSet.name,
                    demoSet.plans.count(),
                )
            }"
            val event = ImportEvent.ImportPublicDemoSet(
                demoSetId = demoSet.id,
                onSuccess = {
                    showSnackbar(successMsg)
                    cont.resume(Unit) { _, _, _ -> }
                },
                onFailure = {
                    showSnackbar("❌ ${importLocalization.current.errors.importError}: $it")
                    cont.resume(Unit) { _, _, _ -> }
                },
            )
            handleEvent(event)
        }
    }
    val onImportPrivate = { accessCode: String ->
        val event = ImportEvent.ImportPrivateDemoSet(
            accessCode = accessCode,
            onSuccess = { showSnackbar("✅ ${importLocalization.current.success.importedPlansCount(it)}") },
            onFailure = { showSnackbar("❌ ${importLocalization.current.errors.importError}: $it") },
        )
        handleEvent(event)
    }
    val onPublish: suspend () -> Unit = {
        suspendCancellableCoroutine { cont ->
            val event = ImportEvent.PublishPlan(
                planId = state.selectedPlan?.firebaseDocId ?: "",
                targetDemoId = state.targetDemoId,
                isPrivate = state.isPrivate,
                onSuccess = {
                    showSnackbar("✅ ${importLocalization.current.success.planPublishedSuccessfully}")
                    cont.resume(Unit) { _, _, _ -> }
                },
                onFailure = {
                    showSnackbar("❌ ${importLocalization.current.errors.publicationError}: $it")
                    cont.resume(Unit) { _, _, _ -> }
                },
            )
            handleEvent(event)
        }
    }
    val onOpenDemoSetDialog = { demoSet: DemoSet ->
        showDialog {
            DemoSetDetailDialogContent(
                demoSet = demoSet,
                onImport = {
                    onImportPublicDemoSet(demoSet)
                    closeDialog()
                },
            )
        }
    }
    val onOpenPlanDialog = {
        showDialog(DialogPanelLargeVariant) {
            PlanDefinitionListSelectionDialogContent(
                onPlanSelected = { planVersion ->
                    handleEvent(ImportEvent.UpdateSelectedPlan(planVersion))
                    closeDialog()
                }
            )
        }
    }
    PageMainContent(modifier) {
        Column(Modifier.fillMaxSize()) {
            AccessCodeSection(
                accessCode = state.accessCode,
                onAccessCodeChange = { handleEvent(ImportEvent.UpdateAccessCode(it)) },
                onImport = onImportPrivate,
            )
            PageSecondaryContent(Modifier.weight(1f)) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(Dimens.pageSectionDefaultGap),
                ) {
                    AvailableDemoSetsSection(
                        demoSets = state.demoSets,
                        onImportDemoSet = onImportPublicDemoSet,
                        onOpenDemoSetDialog = onOpenDemoSetDialog,
                    )
                    if (state.showPublish) {
                        PublishPlanSection(
                            selectedPlan = state.selectedPlan,
                            targetDemoId = state.targetDemoId,
                            isTargetDemoSetPrivate = state.isPrivate,
                            onUpdateTargetDemoId = { handleEvent(ImportEvent.UpdateTargetDemoId(it)) },
                            onUpdateIsPrivate = { handleEvent(ImportEvent.UpdateIsPrivate(it)) },
                            onPublishClicked = onPublish,
                            onPlanSelectClicked = onOpenPlanDialog,
                        )
                    }
                }
            }
        }
    }
}

@Composable
internal fun AccessCodeSection(
    accessCode: String, onAccessCodeChange: (String) -> Unit, onImport: (accessCode: String) -> Unit
) = TitledSection(title = importLocalization.current.accessCode.enterAccessCode) {
    InlineTextInputWithAction(
        value = accessCode,
        variant = InlineInputGhostVariant,
        onValueChange = onAccessCodeChange,
        modifier = Modifier.fillMaxWidth(),
        placeholderText = importLocalization.current.accessCode.enterAccessCodeDescription,
        transformValue = { it.uppercase() },
    ) {
        AppButton(
            text = "",
            onClick = { onImport(accessCode) },
            variant = ButtonRoundVariant.then(ButtonPrimaryVariant),
            icon = {
                UntiledIcon(Assets.IconsUntiled.PROCEED)
            })
    }
}

@Composable
internal fun DemoSetDetailDialogContent(
    demoSet: DemoSet,
    onImport: suspend () -> Unit,
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(Dimens.dialogContentGap)
    ) {
        SpanText(
            demoSet.description,
            modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant, ExuraEmphasisedTextVariant)
        )

        if (demoSet.plans.isNotEmpty()) {
            SpanText(importLocalization.current.demoSets.plansCount(demoSet.plans.size) + ":")

            Column(
                modifier = Modifier.fillMaxWidth().overflow(Overflow.Auto),
            ) {
                demoSet.plans.forEach { plan ->
                    GeneralListEntry(
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        PlanDefinitionDetails(
                            title = plan.title,
                            description = plan.description,
                            numberInstruction = plan.numberInstruction,
                            numberQuestion = plan.numberQuestion,
                            numberPersistent = plan.numberPersistent,
                            numberFloating = plan.numberFloating,
                            planDuration = plan.planDuration,
                            versionId = null,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }
        }

        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
        ) {
            ImportIndicator(onImport)
        }
    }
}

@Composable
private fun ImportIndicator(
    onImport: suspend () -> Unit,
) {
    AsyncButton(
        text = "Import",  // Can use commonLocalization.current.nav.importTemplate if needed
        onClick = onImport,
        variant = ButtonGhostVariant,
        icon = {
            UntiledIcon(Assets.IconsUntiled.IMPORT_PLAN)
        }
    )
}

@Composable
private fun AvailableDemoSetsSection(
    demoSets: List<DemoSet>,
    onImportDemoSet: suspend (DemoSet) -> Unit,
    onOpenDemoSetDialog: (DemoSet) -> Unit,
) = TitledSection(title = importLocalization.current.demoSets.useAvailableDemoSets) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(Dimens.cardEntriesBaseGap),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            demoSets.forEach { demoSet ->
                DemoSetTile(
                    demoSet = demoSet,
                    onImport = { onImportDemoSet(demoSet) },
                    onOpenDialog = { onOpenDemoSetDialog(demoSet) }
                )
            }
        }
    }
}

@Composable
private fun DemoSetTile(
    demoSet: DemoSet,
    onImport: suspend () -> Unit,
    onOpenDialog: () -> Unit,
) {
    GeneralCardEntry(
        variant = InteractiveCardEntryVariant,
        modifier = Modifier
            .width(Dimens.demoSetCardWidth)
            .height(Dimens.demoSetCardHeight)
            .onClick { onOpenDialog() }
            .cursor(Cursor.Pointer)
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            DemoSetDescriptiveContent(demoSet)
            Spacer()
            ImportIndicator(onImport)
        }
    }
}

@Composable
private fun DemoSetDescriptiveContent(
    demoSet: DemoSet,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(Dimens.cardEntryTopBottomSpacer),
    ) {
        SpanText(
            text = demoSet.name,
            modifier = ExuraTextStyle
                .toModifier(ExuraBodyHeaderTextVariant)
                .fillMaxWidth()
                .textAlign(TextAlign.Center)
                .overflow(Overflow.Hidden)
                .whiteSpace(WhiteSpace.NoWrap)
                .textOverflow(TextOverflow.Ellipsis)
        )
        if (demoSet.plans.isNotEmpty()) {
            SpanText(
                text = importLocalization.current.demoSets.plansCount(demoSet.plans.count()),
                modifier = ExuraTextStyle
                    .toModifier(ExuraUnderwriteTextVariant)
            )
        }
        SpanText(
            text = demoSet.description,
            modifier = ExuraTextStyle
                .toModifier(ExuraUnderwriteTextVariant, ExuraCommentaryTextVariant)
                .fillMaxWidth()
                .lineClamp(3)
        )
    }
}

@Composable
private fun PublishPlanSection(
    selectedPlan: Version<BasePlan, PlanDefinitionMetadata>?,
    targetDemoId: String,
    isTargetDemoSetPrivate: Boolean,

    onUpdateIsPrivate: (Boolean) -> Unit,
    onUpdateTargetDemoId: (String) -> Unit,
    onPublishClicked: suspend () -> Unit,
    onPlanSelectClicked: () -> Unit,
) = TitledSection(title = importLocalization.current.publishing.publishPlan) {
    val selectedPlanId = selectedPlan?.firebaseDocId ?: ""
    val isFormValid = selectedPlanId.isNotBlank() && targetDemoId.isNotBlank()
    Column(modifier = Modifier.fillMaxWidth()) {

        ChoosePlanStep(
            selectedPlanId = selectedPlanId,
            onPlanSelectClicked = onPlanSelectClicked,
        )

        CheckChoiceStep(
            selectedPlan = selectedPlan,
        )

        EnterDemoIdStep(
            targetDemoId = targetDemoId,
            isTargetDemoSetPrivate = isTargetDemoSetPrivate,
            onUpdateTargetDemoId = onUpdateTargetDemoId,
            onUpdateIsPrivate = onUpdateIsPrivate,
        )

        PublishStep(
            isFormValid = isFormValid,
            onPublishClicked = onPublishClicked,
        )

    }
}

@Composable
private fun ChoosePlanStep(
    selectedPlanId: String,
    onPlanSelectClicked: () -> Unit,
) {
    StepComponent(
        stepNumber = 1,
        title = importLocalization.current.publishing.stepChoosePlan,
        description = importLocalization.current.publishing.selectPlanToPublish,
    ) {
        UnderwrittenText(
            topAlign = AlignSelf.FlexEnd,
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { if (selectedPlanId.isNotBlank()) SpanText("ID: $selectedPlanId") }
        ) {
            AppButton(
                text = importLocalization.current.publishing.selectPlan,
                onClick = onPlanSelectClicked,
                variant = ButtonPrimaryVariant,
            )
        }
    }
}

@Composable
private fun CheckChoiceStep(
    selectedPlan: Version<BasePlan, PlanDefinitionMetadata>?,
) {
    StepComponent(
        stepNumber = 2,
        title = importLocalization.current.publishing.stepCheckChoice,
        description = importLocalization.current.publishing.makeSureOfPlanSelection,
    ) {
        if (selectedPlan == null) {
            SpanText(importLocalization.current.publishing.noPlanSelected)
        } else {
            val metadata = selectedPlan.metadata
            val fields = metadata?.fields
            val statistics = metadata?.statistics
            PlanDefinitionDetails(
                modifier = Modifier.fillMaxWidth(),
                title = fields?.title ?: "",
                description = fields?.description ?: "",
                versionId = selectedPlan.firebaseDocId,
                planDuration = statistics?.planDuration ?: 0,
                numberInstruction = statistics?.numberInstruction ?: 0,
                numberPersistent = statistics?.numberPersistent ?: 0,
                numberQuestion = statistics?.numberQuestion ?: 0,
                numberFloating = statistics?.numberFloating ?: 0,
            )
        }
    }
}

@Composable
private fun EnterDemoIdStep(
    targetDemoId: String,
    isTargetDemoSetPrivate: Boolean,
    onUpdateTargetDemoId: (String) -> Unit,
    onUpdateIsPrivate: (Boolean) -> Unit,
) {
    StepComponent(
        stepNumber = 3,
        title = importLocalization.current.publishing.stepEnterDemoId,
        description = importLocalization.current.publishing.enterDemoSetId
    ) {
        UnderwrittenText(
            underwrite = {
                Row(horizontalArrangement = Arrangement.spacedBy(Dimens.iconTextRelGapSizeDefault)) {
                    Input(
                        type = InputType.Checkbox,
                        attrs = {
                            checked(isTargetDemoSetPrivate)
                            onChange { onUpdateIsPrivate(it.value) }
                        },
                    )
                    SpanText(importLocalization.current.publishing.makePrivate)
                }
            },
        ) {
            InlineTextInput(
                value = targetDemoId,
                variant = InlineInputGhostVariant,
                onValueChange = onUpdateTargetDemoId,
                modifier = Modifier.fillMaxWidth(),
                placeholderText = importLocalization.current.publishing.demoSetIdPlaceholder,
                transformValue = { it.uppercase() }
            )
        }
    }
}

@Composable
private fun PublishStep(
    isFormValid: Boolean,
    onPublishClicked: suspend () -> Unit,
) {
    StepComponent(
        stepNumber = 4,
        title = importLocalization.current.publishing.stepPublish,
        description = importLocalization.current.publishing.planWillBeAvailable,
    ) {
        UnderwrittenText(
            topAlign = AlignSelf.FlexEnd,
            underwriteVariant = ExuraCommentaryTextVariant.then(ExuraErrorTextVariant),
            underwrite = { if (!isFormValid) SpanText(importLocalization.current.publishing.fillAllFields) }
        ) {
            AsyncButton(
                text = importLocalization.current.publishing.publishButton,
                onClick = onPublishClicked,
                isDisabled = !isFormValid,
                variant = ButtonPrimaryVariant,
                icon = {
                    UntiledIcon(Assets.IconsUntiled.GENERAL_SHARE)
                }
            )
        }
    }
}

@Composable
private fun StepComponent(
    stepNumber: Int,
    title: String,
    description: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    GeneralListEntry(
        modifier = Modifier.cursor(Cursor.Default).then(modifier),
        variant = HoverableListEntryVariant,
    ) {
        StepComponentDescription(
            stepNumber = stepNumber,
            title = title,
            description = description,
        )
        Spacer()
        Box(
            modifier = Modifier.weight(1f),
            contentAlignment = Alignment.CenterEnd,
        ) { content() }
    }
}

@Composable
private fun StepComponentDescription(
    stepNumber: Int,
    title: String,
    description: String,
) {
    Row(
        modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Dimens.listEntryLeftRightSpacer)
    ) {
        NumberedStep(stepNumber)

        UnderwrittenText(
            topVariant = ExuraBodyHeaderTextVariant,
            topAlign = AlignSelf.Start,
            underwriteVariant = ExuraCommentaryTextVariant,
            underwrite = { SpanText(description) }
        ) { SpanText(title, modifier = Modifier.alignSelf(AlignSelf.Start)) }
    }
}
